/* components/patient/Home/DoctorRail.jsx */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query as fq,
  limit,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "../../../firebase";
import AppointmentBooking from "../../../AppointmentBooking";

/* ---------------- LIGHT-MODE ONLY CSS ---------------- */
const lightCss = `
  :root {
    --bg: #f9fafb; --card: rgba(255,255,255,0.94); --text: #111827; --text-light: #6b7280;
    --primary: #4f46e5; --success: #10b981; --border: #e5e7eb; --shadow: 0 25px 50px rgba(0,0,0,0.12);
    --blur: blur(16px); --ring: #4f46e5;
  }
  [data-theme="dark"], .dark, .dark * {
    --bg: #f9fafb; --card: rgba(255,255,255,0.94); --text: #111827; --text-light: #6b7280;
    --primary: #4f46e5; --border: #e5e7eb; --shadow: 0 25px 50px rgba(0,0,0,0.12);
  }
`;

/* ---------------- UI Atoms ---------------- */
const Tag = ({ bg = "rgba(17,24,39,0.06)", color = "#111827", children }) => (
  <span
    style={{
      fontSize: 11,
      padding: "2px 8px",
      borderRadius: 999,
      background: bg,
      color,
      border: `1px solid ${color}22`,
      fontWeight: 700,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    }}
  >
    {children}
  </span>
);

/* ---------------- Helpers ---------------- */
const initials = (name = "Doctor") =>
  name
    .split(" ")
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

async function fastPhoto(doctorId, data) {
  const inline = data?.photoURL || data?.photoUrl || data?.profilePicture;
  if (inline) return inline;
  try {
    const r = ref(storage, `doctor_photos/${doctorId}/profile.jpg`);
    return await getDownloadURL(r);
  } catch {}
  const ini = encodeURIComponent(initials(data?.name));
  return `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'>
    <rect width='100%' height='100%' rx='16' fill='rgb(79,70,229)'/>
    <text x='50%' y='54%' font-size='36' font-family='Inter,Arial' dominant-baseline='middle' text-anchor='middle' fill='white'>${ini}</text>
  </svg>`;
}

/* Specialty → chip style (AL + EN) */
const specialtyMeta = (name = "") => {
  const tag = String(name) || "Mjekësi e përgjithshme";
  const s = tag.toLowerCase();
  let bg = "#e0e7ff",
    color = "#4b5563";
  if (s.includes("mjekësi e përgjithshme") || s.includes("general")) {
    bg = "#e0e7ff";
    color = "#4b5563";
  } else if (s.includes("familjare") || s.includes("family")) {
    bg = "#e0e7ff";
    color = "#334155";
  } else if (s.includes("interne") || s.includes("internal")) {
    bg = "#cffafe";
    color = "#0e7490";
  } else if (s.includes("kardiokirurg")) {
    bg = "#dbeafe";
    color = "#1e40af";
  } else if (s.includes("kardio") || s.includes("cardio")) {
    bg = "#dbeafe";
    color = "#1d4ed8";
  } else if (s.includes("pneumo") || s.includes("pulmo") || s.includes("respir")) {
    bg = "#e0f2fe";
    color = "#0369a1";
  } else if (s.includes("gastro")) {
    bg = "#fee2e2";
    color = "#b91c1c";
  } else if (s.includes("hepato")) {
    bg = "#fee2e2";
    color = "#991b1b";
  } else if (s.includes("endo")) {
    bg = "#ffedd5";
    color = "#c2410c";
  } else if (s.includes("diabet")) {
    bg = "#ffedd5";
    color = "#ea580c";
  } else if (s.includes("nefro")) {
    bg = "#dcfce7";
    color = "#15803d";
  } else if (s.includes("hemato")) {
    bg = "#fee2e2";
    color = "#be123c";
  } else if (s.includes("reumato")) {
    bg = "#ede9fe";
    color = "#6d28d9";
  } else if (s.includes("infekt")) {
    bg = "#fef9c3";
    color = "#a16207";
  } else if (s.includes("alerg") || s.includes("imuno") || s.includes("immun")) {
    bg = "#f0fdf4";
    color = "#166534";
  } else if (s.includes("neurokir")) {
    bg = "#ddd6fe";
    color = "#7c3aed";
  } else if (s.includes("neuro")) {
    bg = "#c7d2fe";
    color = "#6366f1";
  } else if (s.includes("dermat")) {
    bg = "#fed7aa";
    color = "#d97706";
  } else if (s.includes("oftal") || s.includes("ophthal") || s.includes("okul")) {
    bg = "#cffafe";
    color = "#0891b2";
  } else if (s.includes("orl") || s.includes("ent") || s.includes("otol")) {
    bg = "#bbf7d0";
    color = "#0d9488";
  } else if (s.includes("gjinek") || s.includes("obst") || s.includes("gyn")) {
    bg = "#fbcfe8";
    color = "#db2777";
  } else if (s.includes("urolo")) {
    bg = "#bfdbfe";
    color = "#1d4ed8";
  } else if (s.includes("onkolog")) {
    bg = "#fee2e2";
    color = "#dc2626";
  } else if (s.includes("radiologji ndërhyr") || s.includes("interventional")) {
    bg = "#fde68a";
    color = "#92400e";
  } else if (s.includes("radiolog")) {
    bg = "#f5f5f4";
    color = "#3f3f46";
  } else if (s.includes("anestezio") || s.includes("reanim")) {
    bg = "#e5e7eb";
    color = "#111827";
  } else if (s.includes("patolog")) {
    bg = "#f3e8ff";
    color = "#7e22ce";
  } else if (s.includes("urgjenc")) {
    bg = "#fee2e2";
    color = "#b91c1c";
  } else if (s.includes("punës") || s.includes("occupational")) {
    bg = "#e2e8f0";
    color = "#334155";
  } else if (s.includes("sport")) {
    bg = "#dcfce7";
    color = "#16a34a";
  } else if (s.includes("rehabil") || s.includes("fizike") || s.includes("pmr")) {
    bg = "#ccfbf1";
    color = "#0f766e";
  } else if (s.includes("ortop")) {
    bg = "#bfdbfe";
    color = "#2563eb";
  } else if (s === "kirurgji" || s.includes("surgery")) {
    bg = "#fde68a";
    color = "#b45309";
  } else if (s.includes("plastike")) {
    bg = "#fee2e2";
    color = "#e11d48";
  } else if (s.includes("torak")) {
    bg = "#e0f2fe";
    color = "#075985";
  } else if (s.includes("vaskul")) {
    bg = "#fae8ff";
    color = "#a21caf";
  } else if (s.includes("pediatrike")) {
    bg = "#fef3c7";
    color = "#a16207";
  } else if (s.includes("pediatri")) {
    bg = "#bbf7d0";
    color = "#16a34a";
  } else if (s.includes("neonat")) {
    bg = "#e9d5ff";
    color = "#7e22ce";
  } else if (s.includes("geriatr")) {
    bg = "#f5f5f4";
    color = "#525252";
  } else if (s.includes("psikiatri")) {
    bg = "#e9d5ff";
    color = "#9333ea";
  } else if (s.includes("fëmij") && s.includes("psikiatri")) {
    bg = "#ddd6fe";
    color = "#7c3aed";
  } else if (s.includes("logoped")) {
    bg = "#d1fae5";
    color = "#047857";
  } else if (s.includes("dietolog") || s.includes("nutric")) {
    bg = "#dcfce7";
    color = "#15803d";
  } else if (s.includes("laboratorike") || s.includes("laboratory")) {
    bg = "#f0f9ff";
    color = "#0ea5e9";
  } else if (s.includes("mikrobiolog")) {
    bg = "#e0f2fe";
    color = "#0284c7";
  } else if (s.includes("infermier") || s.includes("nurse")) {
    bg = "#ede9fe";
    color = "#5b21b6";
  } else if (s.includes("dentist") || s.includes("dent") || s.includes("stomat")) {
    bg = "#ffe4e6";
    color = "#db2777";
  } else if (s.includes("fizioterap")) {
    bg = "#ccfbf1";
    color = "#0e7490";
  }
  return { bg, color, tag };
};

// Helpers (reused from LandingPage style)
const normalizeKeyword = (s) => (s ?? "").toString().trim().toLowerCase();

const joinText = (...parts) =>
  parts
    .flat()
    .filter(Boolean)
    .map((x) => x.toString())
    .join(" ");

/* ---------------- Main Rail ---------------- */
export default function DoctorRail({
  onDoctorClick,
  max = 999,
  showControls = true,
  externalSearch = "",
  searchQuery,      // external search string (Explore / parent)
  onKeywordPool,    // callback: receives [[keyword, count], ...]
}) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters
  const [specialtyFilter, setSpecialtyFilter] = useState("Të gjitha");
  const [verifiedFilter, setVerifiedFilter] = useState("all");

  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingDoctor, setBookingDoctor] = useState(null);

  // optional local search mirror
  const [searchTerm, setSearchTerm] = useState(externalSearch);
  const effectiveSearch =
    (typeof searchQuery === "string" ? searchQuery : searchTerm) || "";

  useEffect(() => {
    setSearchTerm(externalSearch);
  }, [externalSearch]);

  /* ---- Load Doctors + Verification ---- */
  const load = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      // 1) load verifications
      // expects collection "verifications" with doc.id === doctorId
      let verifiedSet = new Set();
      try {
        const verSnap = await getDocs(collection(db, "verifications"));
        verifiedSet = new Set(verSnap.docs.map((v) => v.id));
      } catch (e) {
        console.warn("DoctorRail: could not load verifications", e);
      }

      // 2) load doctors
      const snap = await getDocs(
        fq(collection(db, "doctors"), limit(300))
      );

      const base = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data() || {};
          const photoUrl = await fastPhoto(d.id, data);
          const score =
            Number(
              data.rankScore ?? data.rankingScore ?? data.score ?? 0
            ) || 0;

          const services = Array.isArray(data.services) ? data.services : [];
          const serviceTitles = services.map((s) => s?.title).filter(Boolean);
          const serviceKeywordsRaw = services.flatMap((s) =>
            Array.isArray(s?.keywords) ? s.keywords : []
          );

          const name = data.name || "Unknown Doctor";
          const specialty = data.specialty || "Mjekësi e përgjithshme";
          const clinicName = data.clinicName || "";
          const location = data.location?.address || data.location || "";
          const languages = Array.isArray(data.languages) ? data.languages : [];
          const bio = data.bio || "";

          const keywords = Array.from(
            new Set(
              [
                ...serviceTitles,
                ...serviceKeywordsRaw,
                name,
                specialty,
                clinicName,
                location.toString(),
              ]
                .map(normalizeKeyword)
                .filter(Boolean)
            )
          );

          const searchCorpus = joinText(
            name,
            specialty,
            clinicName,
            location,
            bio,
            languages,
            serviceTitles,
            serviceKeywordsRaw,
            keywords
          ).toLowerCase();

          return {
            id: d.id,
            name,
            specialty,
            // 🔴 NEW: verified ONLY if doctorId exists in verifications
            isVerified: verifiedSet.has(d.id),
            score,
            clinicName,
            location,
            languages,
            experience: data.experience || "",
            followers:
              typeof data.followerCount === "number"
                ? data.followerCount
                : 0,
            startedPracticeYear: data.startedPracticeYear || "",
            services: serviceTitles,
            phone: data.phone || "",
            email: data.email || "",
            photoUrl,
            keywords,
            searchCorpus,
          };
        })
      );

      base.sort(
        (a, b) => b.score - a.score || a.name.localeCompare(b.name)
      );

      // keyword pool for suggestions
      try {
        if (onKeywordPool) {
          const freq = new Map();
          for (const d of base) {
            const kws = Array.isArray(d.keywords) ? d.keywords : [];
            for (const kw of kws) {
              const k = (kw || "").toString().toLowerCase();
              if (!k) continue;
              freq.set(k, (freq.get(k) || 0) + 1);
            }
          }
          const sorted = Array.from(freq.entries()).sort(
            (a, b) => b[1] - a[1]
          );
          onKeywordPool(sorted);
        }
      } catch (e) {
        console.warn("DoctorRail keyword pool error:", e);
      }

      setAll(base.slice(0, max));
    } catch (e) {
      console.error("DoctorRail load:", e);
      setErr(e.message || String(e));
      setAll([]);
    } finally {
      setLoading(false);
    }
  }, [max, onKeywordPool]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---- Specialty options ---- */
  const specialtyOptions = useMemo(() => {
    const set = new Set(
      all
        .map((d) => d.specialty || "Mjekësi e përgjithshme")
        .filter(Boolean)
    );
    return ["Të gjitha", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [all]);

  /* ---- Filtering & Search ---- */
  const filtered = useMemo(() => {
    const q = effectiveSearch.toLowerCase().trim();
    const terms = q.split(/\s+/).filter(Boolean);

    const spec = specialtyFilter.toLowerCase();
    const vf = verifiedFilter;

    let list = all.filter((d) => {
      // specialty filter
      const dSpec = (d.specialty || "").toLowerCase();
      const matchesSpec =
        spec === "të gjitha" || !spec ? true : dSpec === spec;

      // verified filter (now based on verifications collection)
      const matchesVerified =
        vf === "all"
          ? true
          : vf === "verified"
          ? d.isVerified
          : !d.isVerified;

      if (!matchesSpec || !matchesVerified) return false;

      // search terms
      if (!terms.length) return true;
      const corpus = d.searchCorpus || "";
      return terms.every((t) => corpus.includes(t));
    });

    const hasTerms = terms.length > 0;
    list.sort((a, b) => {
      if (hasTerms) {
        const aHit = terms.some((t) => a.keywords?.includes(t)) ? 1 : 0;
        const bHit = terms.some((t) => b.keywords?.includes(t)) ? 1 : 0;
        if (aHit !== bHit) return bHit - aHit;
      }
      return b.score - a.score || a.name.localeCompare(b.name);
    });

    return list;
  }, [all, specialtyFilter, verifiedFilter, effectiveSearch]);

  const openBookingFor = (doctor) => {
    setBookingDoctor(doctor);
    setBookingOpen(true);
  };

  /* ---- Render ---- */
  return (
    <>
      <style jsx global>{lightCss}</style>
      <style jsx global>{`
        @keyframes floatIn {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .tilt {
          transition: transform 0.2s;
        }
        .tilt:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
        }

        .skeleton {
          background: linear-gradient(
            90deg,
            #f3f4f6 25%,
            #e5e7eb 50%,
            #f3f4f6 75%
          );
          background-size: 200% 100%;
          animation: skeleton 1.5s infinite;
        }
        @keyframes skeleton {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        /* ---------- MOBILE RESPONSIVE ---------- */
        @media (max-width: 640px) {
          section {
            padding: 0 8px;
          }
          .controls {
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
          }
          .controls > * {
            width: 100% !important;
          }
          .card-grid {
            grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)) !important;
            gap: 12px !important;
          }
          .card {
            padding: 14px !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          .card-photo {
            width: 56px !important;
            height: 56px !important;
          }
          .card-title {
            font-size: 16px !important;
          }
          .card-subtitle {
            font-size: 12px !important;
          }
          .card-tags {
            gap: 6px !important;
            flex-wrap: wrap;
          }
          .card-actions {
            flex-direction: column;
            gap: 8px;
          }
          .card-actions button {
            flex: 1;
            min-width: 0;
          }
          .search-input::placeholder {
            font-size: 13px;
          }
        }
        @media (max-width: 480px) {
          .card-photo {
            width: 48px !important;
            height: 48px !important;
          }
          .card-title {
            font-size: 15px !important;
          }
        }
      `}</style>

      <section style={{ margin: "12px 0 32px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 800,
              color: "var(--text)",
            }}
          >
            Gjej mjekë
          </h3>
        </div>

        {/* Controls – TWO comboboxes */}
        {showControls && (
          <div
            className="controls"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <select
              value={specialtyFilter}
              onChange={(e) => setSpecialtyFilter(e.target.value)}
              aria-label="Filtro sipas specialitetit"
              style={{
                border: "1px solid var(--border)",
                borderRadius: 14,
                padding: "11px 14px",
                fontSize: 14,
                background: "white",
              }}
            >
              {specialtyOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <select
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
              aria-label="Filtro sipas verifikimit"
              style={{
                border: "1px solid var(--border)",
                borderRadius: 14,
                padding: "11px 14px",
                fontSize: 14,
                background: "white",
              }}
            >
              <option value="all">Të gjithë mjekët</option>
              <option value="verified">Vetëm të verifikuar</option>
              <option value="unverified">Të pa-verifikuar</option>
            </select>
          </div>
        )}

        {/* Error */}
        {err && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              borderRadius: 12,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {err}
          </div>
        )}

        {/* Grid */}
        <div
          className="card-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 16,
          }}
        >
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="tilt"
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 20,
                  padding: 16,
                  background: "var(--card)",
                  backdropFilter: "var(--blur)",
                  minHeight: 160,
                  animation: "floatIn 0.4s ease-out",
                }}
              >
                <div style={{ display: "flex", gap: 12 }}>
                  <div
                    className="skeleton"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      className="skeleton"
                      style={{
                        height: 18,
                        width: "70%",
                        borderRadius: 6,
                        marginBottom: 6,
                      }}
                    />
                    <div
                      className="skeleton"
                      style={{
                        height: 14,
                        width: "50%",
                        borderRadius: 6,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: 32,
                color: "var(--text-light)",
                fontSize: 14,
                textAlign: "center",
              }}
            >
              Nuk u gjetën mjekë që përputhen me kërkimin.
            </div>
          ) : (
            filtered.map((d) => {
              const meta = specialtyMeta(d.specialty);
              const years =
                /^\d{4}$/.test(String(d.startedPracticeYear))
                  ? new Date().getFullYear() -
                    parseInt(d.startedPracticeYear, 10)
                  : null;

              return (
                <div
                  key={d.id}
                  className="tilt card"
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 20,
                    padding: 16,
                    background: "var(--card)",
                    backdropFilter: "var(--blur)",
                    boxShadow: "var(--shadow)",
                    animation: "floatIn 0.4s ease-out",
                  }}
                >
                  {/* Header */}
                  <div
                    className="card-header"
                    style={{ display: "flex", gap: 12 }}
                  >
                    <div style={{ position: "relative" }}>
                      <img
                        src={d.photoUrl}
                        alt={d.name}
                        className="card-photo"
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 12,
                          objectFit: "cover",
                          boxShadow:
                            "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      />
                      {d.isVerified && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: -4,
                            right: -4,
                            width: 22,
                            height: 22,
                            background: "#10b981",
                            borderRadius: 999,
                            display: "grid",
                            placeItems: "center",
                            boxShadow:
                              "0 2px 6px rgba(0,0,0,0.2)",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => onDoctorClick?.(d.id)}
                          className="card-title"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            margin: 0,
                            cursor: "pointer",
                            color: "var(--text)",
                            fontSize: 16,
                            fontWeight: 800,
                            textAlign: "left",
                          }}
                        >
                          {d.name}
                        </button>
                        <Tag bg={meta.bg} color={meta.color}>
                          {meta.tag}
                        </Tag>
                        <Tag
                          bg="rgba(17,24,39,0.05)"
                          color="#111827"
                        >
                          Score {d.score}
                        </Tag>
                      </div>

                      <div
                        className="card-subtitle"
                        style={{
                          color: "var(--text-light)",
                          fontSize: 12,
                          marginTop: 4,
                        }}
                      >
                        {d.clinicName || "Praktikë e pavarur"}
                        {d.location ? ` • ${d.location}` : ""}
                      </div>

                      <div
                        className="card-tags"
                        style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          marginTop: 6,
                        }}
                      >
                        {d.languages?.slice(0, 3).map((lng, i) => (
                          <Tag
                            key={i}
                            bg="rgba(17,24,39,0.05)"
                            color="#374151"
                          >
                            {lng}
                          </Tag>
                        ))}
                        {years !== null && years >= 0 && (
                          <Tag
                            bg="rgba(34,197,94,0.1)"
                            color="#059669"
                          >
                            {years}+ vite praktikë
                          </Tag>
                        )}
                        {d.followers > 0 && (
                          <Tag
                            bg="rgba(59,130,246,0.1)"
                            color="#2563eb"
                          >
                            {d.followers} followers
                          </Tag>
                        )}
                      </div>

                      {d.services?.length > 0 && (
                        <div
                          style={{
                            color: "var(--text-light)",
                            fontSize: 11,
                            marginTop: 6,
                          }}
                        >
                          Shërbime:{" "}
                          {d.services
                            .slice(0, 2)
                            .join(" • ")}
                          {d.services.length > 2
                            ? " • …"
                            : ""}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div
                    className="card-actions"
                    style={{
                      display: "flex",
                      gap: 8,
                      marginTop: 14,
                    }}
                  >
                    <button
                      onClick={() => onDoctorClick?.(d.id)}
                      style={{
                        flex: 1,
                        background: "transparent",
                        border: "1px solid var(--border)",
                        borderRadius: 14,
                        padding: "10px",
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--text)",
                        cursor: "pointer",
                      }}
                    >
                      View profile
                    </button>

                    <button
                      onClick={() => openBookingFor(d)}
                      style={{
                        flex: 1,
                        background:
                          "linear-gradient(135deg,var(--primary),#7c3aed)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 14,
                        padding: "10px",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        boxShadow:
                          "0 6px 16px rgba(79,70,229,0.3)",
                      }}
                    >
                      Book
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Appointment Modal */}
        {bookingOpen && bookingDoctor && (
          <AppointmentBooking
            doctor={bookingDoctor}
            selectedService={null}
            onClose={() => setBookingOpen(false)}
            onSuccess={() => {
              setBookingOpen(false);
            }}
          />
        )}
      </section>
    </>
  );
}
